import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Baie coulissante', to: '/reparation-baie-coulissante-herault/' },
  { label: 'Serrure & crémone' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Réparation de serrure et crémone de baie coulissante dans l\'Hérault',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'AdministrativeArea', name: 'Hérault' },
  description: 'Serrure, crémone ou poignée de baie coulissante défectueuse dans l\'Hérault ? Réparation et remplacement par nos techniciens.',
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Ma baie coulissante ferme mais ne verrouille plus, que faire ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Ce problème est généralement lié à la crémone ou au crochet de verrouillage. La crémone peut être désalignée, grippée, ou son mécanisme interne peut être cassé. Le crochet peut être usé et ne plus s\'engager correctement dans la gâche. Nos techniciens peuvent diagnostiquer précisément la panne et réparer ou remplacer les pièces défectueuses, restaurant ainsi la sécurité de votre baie.',
      },
    },
    {
      '@type': 'Question',
      name: 'Puis-je sécuriser ma baie en attendant la réparation de la serrure ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'En attendant notre intervention, vous pouvez utiliser une cale de sécurité ou une barre de blocage au sol pour empêcher l\'ouverture du vantail. Évitez les solutions improvisées qui pourraient endommager le cadre ou le vitrage. Nos équipes interviennent sous 24 à 48h dans tout l\'Hérault pour rétablir la sécurité de votre baie coulissante dans les meilleurs délais.',
      },
    },
  ],
};

const contentBlocks = [
  {
    image: '/images/serrure-block1.jpg',
    imageAlt: 'Serrure et cremone de baie coulissante defectueuse DFPRO Herault',
    children: (
      <>
        <h2>La sécurité de votre baie coulissante, notre priorité</h2>
        <p>
          Une baie coulissante qui ne ferme plus correctement, c'est une porte ouverte sur l'extérieur. Au-delà du désagrément, c'est un problème de sécurité pour votre habitation et ses occupants. Le système de verrouillage — composé de la crémone, des crochets, de la poignée et du barillet — est un ensemble mécanique de précision qui, avec le temps et l'usage, peut présenter des défaillances. DFPRO Hérault intervient rapidement à Montpellier, dans sa métropole et sur tout le littoral héraultais pour rétablir la sécurité de votre baie coulissante.
        </p>
        <h3>Les composants du système de fermeture</h3>
        <p>
          Le système de fermeture d'une baie coulissante se compose de plusieurs éléments. La <strong>crémone</strong> est le mécanisme central : actionnée par la poignée, elle commande des tringles qui actionnent les crochets de verrouillage en haut et en bas du vantail. Les <strong>crochets</strong> viennent s'engager dans les gâches fixées au dormant, assurant le verrouillage multipoints. La <strong>poignée</strong>, qu'elle soit à levier ou à rosace avec clé, est l'interface que vous manipulez quotidiennement. Enfin, le <strong>barillet</strong> (si votre baie est équipée d'une serrure à clé) garantit le condamnation de la baie.
        </p>
      </>
    ),
  },
  {
    image: '/images/serrure-block2.webp',
    imageAlt: 'Technicien DFPRO reparant une serrure de baie coulissante',
    imagePosition: 'right' as const,
    children: (
      <>
        <h3>Les pannes les plus fréquentes</h3>
        <p>
          La panne la plus courante est une crémone qui ne commande plus les crochets : la poignée tourne dans le vide sans que le verrouillage ne s'effectue. Cela peut provenir d'une rupture du mécanisme interne de la crémone ou d'un désaccouplement entre la poignée et la crémone. Autre cas fréquent : le crochet de verrouillage ne s'engage plus dans la gâche, souvent à cause d'un désalignement du vantail ou d'une usure du crochet. La poignée peut également se desceller, se bloquer, ou présenter un jeu excessif. Enfin, le barillet peut se gripper ou sa clé peut se casser à l'intérieur.
        </p>
        <h3>Notre approche de réparation</h3>
        <p>
          Face à une serrure ou une crémone défectueuse, nos techniciens commencent par un diagnostic précis pour identifier la pièce en cause. Selon le cas, nous procédons au remplacement de la crémone, au changement des crochets de verrouillage, à la repose ou au remplacement de la poignée, ou au changement du barillet. Toutes les pièces que nous installons sont neuves et compatibles avec votre modèle de menuiserie. Une fois les pièces remplacées, un <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage complet de la baie</Link> est effectué pour garantir un verrouillage parfait et une manœuvre fluide de la poignée.
        </p>
        <h3>Sécurité et tranquillité</h3>
        <p>
          Une baie coulissante qui verrouille correctement, c'est la tranquillité d'esprit pour vous et votre famille. Dans l'Hérault, que vous habitiez une maison avec accès direct au jardin à Montpellier, un appartement en rez-de-chaussée à Béziers ou une villa avec terrasse à Sète, la sécurité de votre baie ne doit jamais être compromise. Avec DFPRO Hérault, vous bénéficiez d'une réparation rapide — sous 24 à 48h, 7j/7 — et d'une garantie de 10 ans sur l'intervention. Devis gratuit et sans engagement.
        </p>
        <p>
          Pour toute question sur nos autres prestations, consultez notre page <Link to="/reparation-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réparation de baie coulissante dans l'Hérault</Link>.
        </p>
      </>
    ),
  },
];

export default function SerrureCremone() {
  return (
    <>
      <SEO
        title="Serrure & crémone de baie coulissante Hérault | DFPRO"
        description="Serrure, crémone ou poignée de baie coulissante cassée dans l'Hérault ? Réparation sécurisée 24-48h. Devis gratuit, garantie 10 ans. 09 80 80 22 42."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/serrure-cremone-baie-coulissante-herault/"
      />
      <ServicePageLayout
        title="Réparation de serrure et crémone de baie coulissante dans l'Hérault"
        subtitle="Verrou, crochet, crémone, poignée : nos techniciens réparent le système de fermeture de votre baie coulissante dans tout l'Hérault."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        heroImage="/images/serrure-hero.jpg"
        heroImageAlt="Serrure et cremone de baie coulissante DFPRO Herault"
        contentBlocks={contentBlocks}
      />
    </>
  );
}